<template>
    <modal @close="closeWindow" :large="true" :class="['reservation-modal', 'dark-top']">
        <div slot="title">
                <h2>Request Refund</h2>
        </div>

        <div slot="body">
            <div class="p-4">
                <!-- Area - Channel Selector -->
                <div class="mt-3">
                    <h5>Channel</h5>
                    <div class="form-group" :class="{ error: $v.refundObject.refundChannels.$error }">
                        <channel-selector v-model="refundObject.refundChannels" @input="channelChanged" :reservation="getSelectedReservation" />

                        <!-- Errors -->
                        <div v-if="$v.refundObject.refundChannels.$error" class="messages">
                            <span v-if="!$v.refundObject.refundChannels.required">Select one.</span>
                        </div>
                    </div>
                </div>

                <!-- Area - Is this a refund request -->
                <div class="mt-3">
                    <h5>When to refund</h5>
                    <div class="form-group" :class="{ error: $v.refundObject.isRequest.$error, 'opacity-50': disableRefundRequestArea}">
                        <div class="d-flex align-items-center p-2 radius-normal bg-lighter pointer" @click="clickRefundRequestType(false)">
                            <div class="mx-2">
                                <radio name="isRequest" :disabled="disableRefundRequestArea" :value="false" v-model="refundObject.isRequest" @input="$v.refundObject.isRequest.$touch"></radio>
                            </div>
                            <div class="darker">
                                <div class="bold">Refund customer now</div>
                            </div>
                        </div>

                        <div class="d-flex align-items-center p-2 radius-normal bg-lighter pointer mt-1" @click="clickRefundRequestType(true)">
                            <div class="mx-2">
                                <radio name="isRequest" :disabled="disableRefundRequestArea" :value="true" v-model="refundObject.isRequest" @input="$v.refundObject.isRequest.$touch"></radio>
                            </div>
                            <div class="darker">
                                <div class="bold">Request refund</div>
                            </div>
                        </div>

                        <div v-if="$v.refundObject.isRequest.$error" class="messages">
                            <span v-if="!$v.refundObject.isRequest.required">Select one.</span>
                        </div>
                    </div>
                </div>

                <!-- Area - Payment Method -->
                <div class="mt-3">
                    <h5>Payment Method</h5>
                    <div class="form-group" :class="{ error: $v.refundObject.paymentType.$error }">
                        <payment-method-picker :allowedMethods="allowdPaymentMethods" @change="paymentMethodPickerChange" v-model="refundObject.paymentType" />

                        <!-- Errors -->
                        <div v-if="$v.refundObject.paymentType.$error" class="messages">
                            <span v-if="!$v.refundObject.paymentType.required">Select one.</span>
                        </div>
                    </div>
                </div>

                <!-- Area - Refund Type-->
                <div class="mt-3">
                    <h5>Refund Type</h5>
                    <div class="form-group" :class="{ error: $v.selectedRefundType.$error, 'opacity-50': disableRefundTypeArea}">
                        <div class="row">
                            <div class="col" v-for="refundType in getRefundTypes">
                                <button class="refundTypeBtn w-100 btn bg-lightest radius-normal darker bold fs-large" :class="{'selected':isRefundTypeSelected(refundType), 'bg-light': disableRefundTypeArea}" @click="selectRefundType(refundType)">{{refundType.label}}</button>
                            </div>
                        </div>

                        <!-- Errors -->
                        <div v-if="$v.selectedRefundType.$error" class="messages">
                            <span v-if="!$v.selectedRefundType.required">Select one.</span>
                        </div>
                    </div>
                </div>

                <!-- Sub option for refund type -->
                <div v-if="selectedRefundType != null" class="">
                    <!-- Area - Type -> Precentage or Value -->
                    <div v-if="selectedRefundType.id == 1" class="mt-3">
                        <h5>Percentage or Value</h5>
                        <div class="form-group" :class="{ error: $v.selectedRefundType.$error }">
                            <div class="col-sm-12 d-flex align-items-center">
                                <div class="bold mr-2">
                                    Value:
                                </div>

                                <div class="d-flex">
                                    <input
                                        type="number"
                                        class="glued-right"
                                        style="width: 100px;"
                                        v-model.number="refundObject.amount"
                                        @input="debouncedValueChanged()"
                                        v-numFloat
                                    >
                                    <v-select class="glued-left" style="width: 50px;" :options="getPercentageOrCurrency" v-model="selectedValueType" @input="valueTypeChanged($event)"></v-select>
                                </div>
                            </div>

                            <!-- Errors -->
                            <div v-if="$v.selectedRefundType.$error" class="messages">
                                <span v-if="!$v.selectedRefundType.isValid">Invalid value.</span>
                            </div>
                        </div>
                    </div>

                    <!-- Area - Type -> Reduce Quantity (Pax and Extras)-->
                    <div v-if="selectedRefundType.id == 2" class="mt-3">
                        <h5>Reservation Items</h5>
                        <div class="form-group" :class="{ error: $v.refundItems.$error }">
                            <div class="col-sm-12">
                                <div class="w-100 text-center py-4" v-if="calculationResult == null">
                                    <loader />
                                </div>
                                <div v-else>
                                    <reduce-quantity v-model="refundObject.refundItems" v-if="calculationResult != null" :calculations="calculationResult" @change="requestCalculations()" />

                                    <div class="d-flex align-items-center p-2 radius-normal bg-lighter pointer" @click="toggleReleaseQuantity">
                                        <div class="mx-2">
                                            <checkbox v-model="refundObject.isToReleaseQuantity"></checkbox>
                                        </div>
                                        <div class="darker">
                                            <div class="bold">Make refunded items available again</div>
                                            <div class="fs-smaller">This will allow to re-sell refunded items</div>
                                        </div>
                                    </div>

                                    <!-- Errors -->
                                    <div v-if="$v.refundItems.$error" class="messages">
                                        <span v-if="!$v.refundItems.isValid">Select at least one item to refund.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Area - Reason -->
                <div class="mt-3">
                    <h5>Reason</h5>
                    <div class="form-group" :class="{ error: $v.refundObject.refundReasonType.$error }">
                        <v-select :options="getRefundReasons" :disabled="disableReasonArea" label="text" placeholder="Select reason" v-model="selectedReason" @input="reasonChanged"></v-select>

                        <div v-if="refundObject.refundReasonType == 0" class="mt-3">
                            <label>Describe the refund reason</label>
                            <input type="text" class="w-100" v-model="refundObject.reason">
                        </div>

                        <!-- Errors -->
                        <div v-if="$v.refundObject.refundReasonType.$error" class="messages">
                            <span v-if="!$v.refundObject.refundReasonType.required">Reason is required.</span>
                        </div>
                    </div>
                </div>

                <!-- Area - Value to Refund -->
                <div class="mt-3">
                    <h5>Value to Refund</h5>
                    <div class="form-group">
                        <div class="fs-large bold dark text-right">
                            <loader v-if="isLoadingCalculations" />
                            <div v-else class="d-flex flex-column">
                                <div v-if="refundValueProcessed.now != null" class="d-flex flex-grow-1 justify-content-end">
                                    <span>Refund now: {{formatPrice(refundValueProcessed.now)}} €</span>
                                </div>
                                <div v-if="refundValueProcessed.later != null" class="d-flex flex-grow-1 justify-content-end">
                                    <span>Refund later: {{formatPrice(refundValueProcessed.later)}} €</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div slot="footer" class="align-items-center">
            <div class="color-6 w-100 text-align-right">
                {{ emailWarningMessage }}
            </div>
            <button type="button" class="btn cancel wider" @click="clickCancel">Cancel</button>
            <button type="button" class="btn ok wider" :class="{'disabled':isRefundValid == false}" @click="clickRefund" :disabled="getIsLoadingCalculations">Confirm</button>
        </div>
    </modal>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'
    import { required, email, minLength, not, requiredUnless } from 'vuelidate/lib/validators'
    import paymentMethodPicker from '@/tools/PaymentMethodPicker'
    import Modal from './Modal'
    import extendedVSelect from '@/extends/vue-select'
    import Loader from '@/tools/loaders/loader1'
    import _ from 'lodash'

    import channelSelector from '@/components/reservations/refund/ChannelSelector'
    import reduceQuantity from '@/components/reservations/refund/ReduceQuantity'

    import { reservationsMixin } from '@/mixins/reservations'

    export default {
        mixins: [ reservationsMixin ],
        created() {
            this.debouncedValueChanged = _.debounce(this.valueChanged, 500);
            // Select percent
            this.selectedValueType = this.getPercentageOrCurrency[0];

            // Search for existing not saved refund (id undefined or 0)
            let foundRefund = this.getSelectedReservation.refunds.find(refund => typeof refund.id == "undefined" || refund.id == 0);

            // Use a virgin refund or reuse an existing one (id = 0)
            if (!foundRefund || this.getSelectedReservation.refunds.length == 0) {
                this.$set(this, 'refundObject', JSON.parse(JSON.stringify(this.refundObjectClean)));
                this.refundObject.userAccount = this.account;
            } else {
                // Find the refund with id 0 (or without id)
                this.refundObject = foundRefund;

                // Select the correct refundType
                if (foundRefund.isToRefundTotal == true) {
                    this.selectedRefundType = this.getRefundTypes.find(refundType => refundType.id == 0);
                } else if (foundRefund.refundItems.length > 0) {
                    this.selectedRefundType = this.getRefundTypes.find(refundType => refundType.id == 2);
                } else {
                    this.selectedRefundType = this.getRefundTypes.find(refundType => refundType.id == 1);
                }

                // Select the correct parcial refund type
                this.selectedValueType = this.getPercentageOrCurrency.find(item => item.id == this.refundObject.amountType);
                this.refundObject.amountType;

                // Select correct reason
                this.selectedReason = this.getRefundReasons.find(reason => reason.id == this.refundObject.refundReasonType);

                // Request calculations
                this.requestCalculations();
            }
        },
        mounted() {
            // Preselected - Make refunded items available again
            this.refundObject.isToReleaseQuantity = true;
        },
        data() {
            return {
                selectedRefundType: null,
                selectedValueType: null,
                selectedPaymentMethod: null,
                selectedReason: null,
                refundObjectClean: {
                    refundChannels: [],
                    reason: null,
                    refundReasonType: null,
                    amount: 0,
                    amountType: 1, // Start with percentage (1) //amountTypeEnum.code
                    isToReleaseQuantity: false,
                    isToRefundAtSource: false,
                    isToRefundTotal: false,
                    isRequest: null, // Was null but now every refund is a request so is set true
                    paymentType: null, // PaymentTypeEnum.cash
                    userAccount: null,
                    refundItems: []
                },
                refundObject: null,
                isLoadingCalculations: false,
                refundValue: 0,
                calculationResult: null,
                debouncedValueChanged: null
            }
        },
        validations: {
            selectedRefundType: {
                required,
                isValid: (val, vm) => {
                    if(val == null){
                        return false
                    } else {
                        if (val.id != 1) {
                            return true
                        } else {
                            // Is Percentage
                            if (vm.refundObject.amountType == 1) {
                                return Number(vm.refundObject.amount) > 0 && Number(vm.refundObject.amount) <= 100;
                            } else {
                                // Is Euro
                                return Number(vm.refundObject.amount) > 0;
                            }
                        }
                    }
                }
            },
            refundObject: {
                refundChannels: {
                    required,
                },
                refundReasonType: {
                    required,
                },
                paymentType: {
                    required,
                },
                isRequest: {
                    required: (val) => {
                        return val != null;
                    }
                },
            },
            refundItems: {
                isValid: (val, vm) => {
                    if (vm.selectedRefundType == null) {
                        return true
                    }

                    if (vm.selectedRefundType.id != 2) {
                        return true;
                    } else {
                        if (vm.refundObject.refundItems && vm.refundObject.refundItems.length > 0) {
                            return vm.refundObject.refundItems.filter(refundItem => refundItem.quantity > 0).length > 0;
                        } else {
                            return false;
                        }
                    }
                }
            }
        },
        computed: {
            ...mapGetters([
                'getSelectedReservation',
                'getReservationCalculations',
                'getRefundTypes',
                'getPercentageOrCurrency',
                'account',
                'getRefundAvailablePaymentMethods',
                'getRefundReasons',
                'getPaymentTypeListAll',
                'getIsLoadingCalculations'
            ]),
            disableRefundRequestArea() {
                return !this.refundObject.refundChannels || this.refundObject.refundChannels.length < 1;
            },
            disableRefundTypeArea() {
                return this.refundObject.paymentType == null;
            },
            disableReasonArea() {
                return this.selectedRefundType == null;
            },
            refundValueProcessed() {
                if (this.refundObject.isRequest) {
                    return {
                        now:  0,
                        later: this.refundValue
                    }
                } else {
                    return {
                        now: this.refundValue,
                        later: null
                    }
                }
            },
            allowdPaymentMethods() {
                // ALLOW ALL FOR ALL!!!!!!
                if (this.refundObject.refundChannels.length > 0 && this.refundObject.isRequest != null) {
                    return this.getPaymentTypeListAll.map(item => item.id);
                } else {
                    return [];
                }

                // TABLE
                //                 AGENT 	         MY TEAM 	    WIDGET 	           GYG 	       LOCALS
                //             Refund  request Refund  Request Refund 	Request Refund 	Request Refund 	Request
                // Cash        x                   x 		    a               a               a
                // Debit Card  x                   x 		    a               a               a
                // Credit Card x                   x 		    a               a               a
                // Paypal                              x
                // Paypal                              x
                // BankTransfer                        x
                // AgentDeposit       x                                                 x               x
                // Stripe                                               x
                // Redunicre                           x
                //-----------------------------------------------------------------------------------------------
                // a = Only admin can do it
                // x = "Anyone" can
                //-----------------------------------------------------------------------------------------------
                // Cash = 1
                // Debit Card = 2
                // Credit Card = 3
                // Paypal = 5
                // Bank Transfer = 6
                // Agent Deposit = -100
                // Stripe = -90
                // Redunicre = 7

                let output = [];
                // If no channel is selcted
                if (this.refundObject.refundChannels.length == 0 || this.refundObject.isRequest == null) {
                    return [];
                }

                let isRefund = this.refundObject.isRequest == true? false : true;
                let foundRefundAvailablePaymentMethod = this.getRefundAvailablePaymentMethods.find(item => item.channelType == this.refundObject.refundChannels[0].channelType);
                
                // Now we search for specific permissions for the logged user (example: admin -> accessType = 1)
                let userAccessType = this.account.accessType;
                let foundUserPermissions = foundRefundAvailablePaymentMethod.permissions.find(permission => permission.accessType == userAccessType);
                let currentUserPermissionRefund = [];
                let currentUserPermissionRequest = [];

                if (foundUserPermissions) {
                    currentUserPermissionRefund = foundUserPermissions.refund;
                    currentUserPermissionRequest = foundUserPermissions.request;
                }

                // Get generic permissions and Merge the user specific permissions with the "generic permissions".
                // This will result on repeated number on the arry but for our that is not a problem
                let refund = foundRefundAvailablePaymentMethod.refund.concat(currentUserPermissionRefund);
                let request = foundRefundAvailablePaymentMethod.request.concat(currentUserPermissionRequest);

                if (foundRefundAvailablePaymentMethod) {
                    if (isRefund) {
                        output = refund;
                    } else {
                        output = request;
                    }
                } else {
                    output = [];
                }

                return output;
            },
            isRefundValid() {
                return this.$v.selectedRefundType.$invalid == false && this.$v.refundObject.$invalid == false && this.$v.refundItems.$invalid == false;
            },
            soldPax() {
                if (this.refundObject.refundChannels.length > 0) {
                    return this.getSelectedReservation.bookingGenderPrices.filter(soldItem => soldItem.channelType == this.refundObject.refundChannels[0].channelType);
                } else {
                    return this.getSelectedReservation.bookingGenderPrices;
                }
            },
            soldExtras() {
                if (this.refundObject.refundChannels.length > 0) {
                    return this.getSelectedReservation.bookingComponents.filter(soldItem => soldItem.channelType == this.refundObject.refundChannels[0].channelType);
                } else {
                    return this.getSelectedReservation.bookingComponents;
                }
            },
            emailWarningMessage() {
                return `Customer ${this.refundObject && this.refundObject.refundChannels && this.refundObject.refundChannels[0] && this.refundObject.refundChannels[0].channelType? 'and Agent':''} will receive an email with refund information.`;
            }
        },
        methods: {
            ...mapActions([
                'closeModal',
                'setReservationCalculations',
                'setSelectedReservation'
            ]),
            channelChanged() {
                // Clear refund type | Clear payment method
                this.refundObject.paymentType = null;

                if (this.selectedRefundType != null) {
                    this.selectedRefundType = null;
                    this.updateAvailableRefundItems();
                }
            },
            createRefundItemsArray() {
                let output = [];
                let cleanPax = [];
                let cleanExtras = [];

                // PAX
                if (this.soldPax && this.soldPax.length > 0) {
                    cleanPax = JSON.parse(JSON.stringify(this.soldPax));
                    cleanPax = cleanPax.filter(pax => {return pax.quantityToBook > 0});
                    cleanPax = cleanPax.map(pax => {
                        return {
                            refundType: null,
                            quantity: 0,
                            bookingGenderPrice: pax
                        }
                    });
                }

                // EXTRAS
                if (this.soldExtras && this.soldExtras.length > 0) {
                    cleanExtras = JSON.parse(JSON.stringify(this.soldExtras));
                    cleanExtras = cleanExtras.map(extra => {
                        return {
                            refundType: null,
                            quantity: 0,
                            bookingComponent: extra
                        }
                    });
                }

                output = output.concat(cleanPax, cleanExtras);

                return output;
            },
            paymentMethodPickerChange(e) {
                if (e == null) {
                    this.refundObject.paymentType = null;
                }
                this.$v.refundObject.paymentType.$touch;
            },
            valueTypeChanged(e) {
                this.refundObject.amountType = e.id;
                this.updateAmountResumeValue();
                this.requestCalculations();
            },
            valueChanged() {
                this.updateAmountResumeValue();
                this.requestCalculations();
            },
            updateAmountResumeValue() {
                const newValue = this.refundObject.amount !== 0 ? 0 : this.total;
                this.$set(this.refundObject, 'amountResumeValue', newValue);
            },
            updateAvailableRefundItems() {
                this.refundObject.refundItems = this.createRefundItemsArray();
                this.requestCalculations();
            },
            selectRefundType(refundType) {
                if (this.disableRefundTypeArea) {
                    return false;
                }

                this.selectedRefundType = refundType;

                // Set total payment or parcial payment
                const isTotalOrPartial = refundType.id === 0 || refundType.id === 1;

                if (isTotalOrPartial) {
                    this.$set(this.refundObject, 'refundItems', []);
                    this.refundObject.isToRefundTotal = refundType.id === 0;
                    this.refundObject.amount = refundType.id === 0 ? null : 0;
                    this.updateAmountResumeValue();
                    this.requestCalculations();
                } else if (refundType.id === 2) {
                    // Reduce quantity
                    this.updateAvailableRefundItems();
                    this.refundObject.amount = null;
                    this.refundObject.isToRefundTotal = false;
                }
            },
            isRefundTypeSelected(refundType) {
                return this.selectedRefundType != null && this.selectedRefundType.id == refundType.id;
            },
            clickRefundRequestType(e) {
                if (this.disableRefundRequestArea) {
                    return false;
                }

                // Reset downstream settings
                this.selectedRefundType = null;
                this.refundObject.paymentType = null;

                this.refundObject.isRequest = e;
                this.$set(this.refundObject, 'paymentType', null);
                this.$v.refundObject.isRequest.$touch;
            },
            toggleReleaseQuantity() {
                this.refundObject.isToReleaseQuantity = !this.refundObject.isToReleaseQuantity;
            },
            clickCancel() {
                this.closeModal();
            },
            updateReservation() {
                if (this.refundObject.isToReleaseQuantity == true) {
                    // Update Reservation PAX and Extra count
                    if (this.refundObject.refundItems && this.refundObject.refundItems.length > 0) {
                        // Iterate all refund items
                        for (let refundItem of this.refundObject.refundItems) {
                            // Is Pax
                            if (refundItem.bookingGenderPrice) {
                                // Find the same Pax on reservation and update its quantityToBook
                                let foundReservationGenderPrice = this.getSelectedReservation.bookingGenderPrices.find(bookingGenderPrice => {
                                    return bookingGenderPrice.id == refundItem.bookingGenderPrice.id;
                                });

                                if (foundReservationGenderPrice) {
                                    foundReservationGenderPrice.quantityToBook -= refundItem.quantity;

                                    // Reduce the value of paid and ticket price (totalProductGrossprice, totalGrossprice) based on reduced quantity
                                    this.getReservationCalculations.totalProductGrossprice -= (foundReservationGenderPrice.grossprice * refundItem.quantity);
                                    this.getReservationCalculations.totalGrossprice -= (foundReservationGenderPrice.grossprice * refundItem.quantity);
                                }
                            } else if (refundItem.bookingComponent) {
                                // Is Extra | Find the same Extra on reservation and update its quantity
                                let foundReservationExtra = this.getSelectedReservation.bookingComponents.find(bookingComponent => {
                                    return bookingComponent.id == refundItem.bookingComponent.id
                                });

                                if (foundReservationExtra) {
                                    foundReservationExtra.quantity -= refundItem.quantity;
                                }
                            }
                        }
                    }
                }

                // Update stored reservation
                this.setSelectedReservation({payload: this.getSelectedReservation, isToResetFlags: false});
            },
            reasonChanged(e) {
                this.refundObject.refundReasonType = e.id;
            },
            addRefundToReservation(refund, targetReservation) {
                // Search if we have a current editing refund
                let foundEditingRefund = targetReservation.refunds.find(reservationRefund => {
                    return typeof reservationRefund.id == 'undefined' || reservationRefund.id == 0
                });

                if (foundEditingRefund) {
                    foundEditingRefund = refund;
                } else {
                    targetReservation.refunds.push(refund);
                }
            },
            requestCalculations() {
                this.isLoadingCalculations = true;
                let tempReservation = JSON.parse(JSON.stringify(this.getSelectedReservation));
                
                // Add refund to reservation
                this.addRefundToReservation(this.refundObject, tempReservation);
                this.setReservationCalculations({
                    reservation:tempReservation,
                    onlyCalculate: true
                }).then(response => {
                    if (response && response.data) {
                        this.calculationResult = response.data;
                        let refundId = this.refundObject.id ? this.refundObject.id : 0;
                        this.refundValue = response.data.refund.amountResumeValue;
                        this.refundObject.amountResumeValue = response.data.refund.amountResumeValue;
                    }
                    this.isLoadingCalculations = false;
                }).catch(err => {
                    this.isLoadingCalculations = false;
                })
            },
            addPayout(reservation) {
                let payoutObject = {
                    tempRefundPayout: true, // We use this to know if this payout comes from the current refund, if we edit the refund we update this payout
                    currency: "€", // Calculations always returns the € value so we can hardcode this
                    description: "Refund",
                    paymentDirection: 1, // 1 = out | 1 = in
                    treasurerUserAccount: this.account,
                    type: this.refundObject.paymentType, // Payment method
                    value: this.refundValue,
                }

                // Search for a tempRefundPayout payout
                let tempPayout = this.getSelectedReservation.billing.payments.find(payment => payment.tempRefundPayout == true);
                if (tempPayout) {
                    let paymentIndex = this.getSelectedReservation.billing.payments.indexOf(tempPayout);
                    this.getSelectedReservation.billing.payments[paymentIndex] = payoutObject;
                } else {
                    this.getSelectedReservation.billing.payments.push(payoutObject);
                }
            },
            clickRefund() {
                this.$v.$touch();

                if (this.isRefundValid == false) {
                    return false;
                }

                // Save refund items to new array to show on "Refunds" tab
                this.$set(this.refundObject, 'refundDetailsItems',this.refundObject.refundItems);

                this.addRefundToReservation(this.refundObject, this.getSelectedReservation);

                this.$set(this.getSelectedReservation.updateFlags, 'refund', true);
                this.setReservationCalculations(this.getSelectedReservation).then(calculations => {
                    this.updateReservation();
                    this.closeModal();
                });
            },
            closeWindow() {
                this.setReservationCalculations(this.getSelectedReservation);
                this.closeModal();
            },
        },
        components: {
            Modal,
            Loader,
            paymentMethodPicker,
            vSelect: extendedVSelect,
            reduceQuantity,
            channelSelector
        }
    }
</script>

<style lang="scss" scoped>
    @import "~@/assets/scss/_utility.scss";

    .refundTypeBtn {
        @extend .border-medium;
        height: 60px;

        &:hover {
            @extend .bg-lighter;
        }

        &.selected {
            @extend .border-darker;
            border-width: 2px !important;
            background-color: $color-3 !important;
            color: $lightest !important;
        }
    }
</style>



// WEBPACK FOOTER //
// ReservationRefund.vue?6c2394ae